import React, { FC } from 'react';
import {
	ConfidenceLabel,
	Recommendation,
	RepairReplaceBands,
} from '../../types/asset_replacement_score';
import { zoneFromScore, confidenceTier, BAR_COLOR } from './colours';

const GAP_PX = 4; // matches Tailwind gap-2xs
const MARKER_WIDTH_PX = 12;

interface ZoneProgressBarProps {
	markerPosition: number | null;
	bands: RepairReplaceBands;
	confidence: ConfidenceLabel;
	showLabels?: boolean;
	labels?: [string, string, string];
	// When true, forces the grey fill + hides the marker — used when a user
	// override is in effect, so the computed score isn't visually emphasised.
	muted?: boolean;
}

export const ZoneProgressBar: FC<ZoneProgressBarProps> = ({
	markerPosition,
	bands,
	confidence,
	showLabels = true,
	labels = ['Low', 'Medium', 'High'],
	muted = false,
}) => {
	const repairWidth = bands.repair[1] - bands.repair[0];
	const monitorWidth = bands.monitor[1] - bands.monitor[0];
	const replaceWidth = bands.replace[1] - bands.replace[0];

	const hasMarker = !muted && markerPosition !== null && markerPosition !== undefined;
	const clampedScore = hasMarker ? Math.min(Math.max(markerPosition as number, 0), 1) : 0;

	const repairFill = muted
		? 1
		: repairWidth > 0
		? Math.min(clampedScore, bands.repair[1]) / repairWidth
		: 0;
	const monitorFill = muted
		? 1
		: monitorWidth > 0
		? Math.max(0, Math.min(clampedScore, bands.monitor[1]) - bands.monitor[0]) / monitorWidth
		: 0;
	const replaceFill = muted
		? 1
		: replaceWidth > 0
		? Math.max(0, clampedScore - bands.replace[0]) / replaceWidth
		: 0;

	// Segments are laid out in a 3-column grid with two GAP_PX gaps, so the
	// track widths sum to (container - 2 * GAP_PX). To land the marker's tip
	// at the true score position: subtract the score's share of gap space,
	// add back one gap per boundary already crossed, then subtract half the
	// marker width so its visual centre (not its left edge) aligns.
	const gapsCrossed = clampedScore < bands.repair[1] ? 0 : clampedScore < bands.monitor[1] ? 1 : 2;
	const totalGap = 2 * GAP_PX;
	const markerLeft = `calc(${clampedScore * 100}% - ${clampedScore * totalGap}px + ${
		gapsCrossed * GAP_PX
	}px - ${MARKER_WIDTH_PX / 2}px)`;

	const totalWidth = repairWidth + monitorWidth + replaceWidth;
	const repairFr = totalWidth > 0 ? repairWidth * 100 : 40;
	const monitorFr = totalWidth > 0 ? monitorWidth * 100 : 30;
	const replaceFr = totalWidth > 0 ? replaceWidth * 100 : 30;
	const gridTemplate = `${repairFr}fr ${monitorFr}fr ${replaceFr}fr`;

	// Current zone derived from the marker; all filled segments use that zone's
	// colour at the current confidence tier.
	const currentZone: Exclude<Recommendation, 'insufficient_data'> = zoneFromScore(
		clampedScore,
		bands
	);
	const tier = muted ? 'low' : confidenceTier(confidence);
	const fillColor = BAR_COLOR[currentZone][tier];

	return (
		<div className="mt-4">
			<div className="relative">
				{hasMarker ? (
					<div
						className="absolute -top-3 z-10 text-center leading-none text-character-secondary"
						style={{ left: markerLeft, width: `${MARKER_WIDTH_PX}px` }}
					>
						▼
					</div>
				) : null}
				<div className="grid h-6 gap-2xs" style={{ gridTemplateColumns: gridTemplate }}>
					<div className="h-full overflow-hidden rounded bg-grey-4">
						<div
							className="h-full"
							style={{ width: `${repairFill * 100}%`, backgroundColor: fillColor }}
						/>
					</div>
					<div className="h-full overflow-hidden rounded bg-grey-4">
						<div
							className="h-full"
							style={{ width: `${monitorFill * 100}%`, backgroundColor: fillColor }}
						/>
					</div>
					<div className="h-full overflow-hidden rounded bg-grey-4">
						<div
							className="h-full"
							style={{ width: `${replaceFill * 100}%`, backgroundColor: fillColor }}
						/>
					</div>
				</div>
			</div>
			{showLabels ? (
				<div
					className="footnoteregular mt-2 grid gap-2xs uppercase tracking-wide text-character-secondary"
					style={{ gridTemplateColumns: gridTemplate }}
				>
					<div className="text-center">{labels[0]}</div>
					<div className="text-center">{labels[1]}</div>
					<div className="text-center">{labels[2]}</div>
				</div>
			) : null}
		</div>
	);
};

export default ZoneProgressBar;
